/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — page composition.
 *
 * Composes the deviation-ranked VitalsTable behind a filter bar (status chips +
 * name search + a living-environment chip group) and a shared DetailDrawer.
 * Filtering is delegated to the pure vitalsFilterModel so this component stays
 * presentational; selecting a client opens the full VitalsDetailPanel in the
 * drawer (per-vital 24h sparklines, active alerts, device source + battery).
 *
 * Presentational + fail-safe: rows are injected by the route (so this is
 * testable) and default to []; a malformed row can't break the render. Colors,
 * fonts, and severity vocabulary come from the single design-token set.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import VitalsTable from "../../components/web/VitalsTable";
import VitalsDetailPanel from "../../components/web/VitalsDetailPanel";
import DetailDrawer from "../../components/web/DetailDrawer";
import { VITALS_STATUS_FILTERS, VITALS_ENV_FILTERS, filterVitalsRows } from "./vitalsFilterModel";
import { theme } from "../../styles/designTokens";

function chipStyle(active) {
  return {
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 13,
    fontFamily: theme.fontUI,
    cursor: "pointer",
    border: `1px solid ${active ? theme.textPrimary : theme.border}`,
    background: active ? theme.surface : "transparent",
    color: active ? theme.textPrimary : theme.textMuted,
  };
}


export default function VitalsPage({ rows = [], now, staleMinutes = 15 }) {
  const [status, setStatus] = useState("all");
  const [livingEnv, setLivingEnv] = useState("all");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null); // selected row | null

  const filtered = filterVitalsRows(rows, { status, livingEnv, query, now, staleMinutes });

  return (
    <div
      data-testid="vitals-page"
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <div
        data-testid="vitals-filter-bar"
        style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}
      >
        {VITALS_STATUS_FILTERS.map((f) => {
          const active = f.key === status;
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`vitals-filter-${f.key}`}
              aria-pressed={active}
              onClick={() => setStatus(f.key)}
              style={chipStyle(active)}
            >
              {f.label}
            </button>
          );
        })}
        <input
          type="search"
          aria-label="Search clients by name"
          placeholder="Search clients…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            marginLeft: "auto",
            minWidth: 200,
            padding: "6px 10px",
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            background: theme.surface,
            color: theme.textPrimary,
            fontFamily: theme.fontUI,
            fontSize: 13,
          }}
        />
      </div>

      <div
        role="group"
        aria-label="Filter by living environment"
        data-testid="vitals-env-bar"
        style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}
      >
        {VITALS_ENV_FILTERS.map((f) => {
          const active = f.key === livingEnv;
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`vitals-env-${f.key}`}
              aria-pressed={active}
              // Disambiguate the env "All" from the status "All" for screen
              // readers (visible "All" is contained in the accessible name, so
              // WCAG "Label in Name" still holds); the others are unambiguous.
              aria-label={f.key === "all" ? "All living environments" : undefined}
              onClick={() => setLivingEnv(f.key)}
              style={chipStyle(active)}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <VitalsTable
        rows={filtered}
        now={now}
        staleMinutes={staleMinutes}
        onSelectClient={(row) => setDetail(row)}
      />

      <DetailDrawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? detail.clientName || "Client detail" : ""}
        type="client"
      >
        <VitalsDetailPanel row={detail} />
      </DetailDrawer>
    </div>
  );
}

// `rows` is normally an array, but the component fail-safes on a null/missing
// value (renders the empty triage table) — so the prop type permits null
// explicitly rather than emitting a PropTypes warning on that supported path.
function arrayOrNull(props, propName, componentName) {
  const value = props[propName];
  if (value !== null && value !== undefined && !Array.isArray(value)) {
    return new Error(
      `Invalid prop \`${propName}\` supplied to \`${componentName}\`: expected an array or null.`,
    );
  }
  return null;
}

VitalsPage.propTypes = {
  /** Triage rows: { id, clientName, status, vitals, baselines, lastUpdate }. */
  rows: arrayOrNull,
  /** Reference time for elapsed/staleness display. */
  now: PropTypes.number,
  /** Minutes after which a reading is stale. */
  staleMinutes: PropTypes.number,
};
